/**
 * useToggleApp Job Hooks
 *
 * Custom React Query mutation hooks for pausing/resuming appJobs.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appJobService } from '../../../api/app-job.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseToggleAppJobOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for pausing a appJob
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const usePauseAppJob = (
  tenantId: string,
  options?: UseToggleAppJobOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appJobId: string) =>
      appJobService.pauseAppJob(tenantId, appJobId),
    onSuccess: () => {
      // Invalidate all appJob queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['appJobs', tenantId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['appJob', tenantId],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};

/**
 * Hook for resuming a appJob
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useResumeAppJob = (
  tenantId: string,
  options?: UseToggleAppJobOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appJobId: string) =>
      appJobService.resumeAppJob(tenantId, appJobId),
    onSuccess: () => {
      // Invalidate all appJob queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['appJobs', tenantId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['appJob', tenantId],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
